import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";



import Calendar from "../components/Calendar";

import { useExam } from "../context/ExamContext";



import { scheduleExam } from "../api/client";



import AppSidebar from "../pages/AppSidebar";



/* =========================================================

   DATE HELPERS

   ========================================================= */



function getTodayISO() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getCurrentTimeHHMM() {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}


function toDisplayDate(iso) {

  if (!iso) {

    return "—";

  }



  const [year, month, day] = iso.split("-");



  return `${day}/${month}/${year}`;

}



function formatTime(time) {

  if (!time) {

    return "—";

  }



  const [hours, minutes] = time

    .slice(0, 5)

    .split(":")

    .map(Number);



  const period = hours >= 12 ? "PM" : "AM";



  const displayHour =

    hours % 12 === 0 ? 12 : hours % 12;



  return `${String(displayHour).padStart(2, "0")}:${String(

    minutes

  ).padStart(2, "0")} ${period}`;

}



/* =========================================================

   COMPONENT

   ========================================================= */



export default function SelectDateTime() {

  const navigate = useNavigate();



  const {

    selectedCourse,

    selectedExam,

    registrationId,

    paymentStatus,

    schedule,

    setSchedule,

  } = useExam();



  /*

   * The actual examination should normally be in selectedExam.

   * selectedCourse is kept as a fallback for compatibility.

   */

  const exam = selectedExam || selectedCourse;



  /* =======================================================

     STATE

     ======================================================= */



  const [date, setDate] = useState(

    schedule?.date || null

  );



  const [startTime, setStartTime] = useState(

    schedule?.start_time || ""

  );



  const [scheduleError, setScheduleError] = useState("");

  const [scheduleSuccess, setScheduleSuccess] = useState("");

  const [busy, setBusy] = useState(false);



  /*
   * The student can select today or any future date immediately
   * after successful payment. There is NO 24-hour waiting period.
   * If today is selected, only a future time is allowed.
   */
  const minDate = getTodayISO();

  const maxDate = "2027-12-31";

  const isTodaySelected = date === minDate;
  const minTime = isTodaySelected ? getCurrentTimeHHMM() : "";

  /* =======================================================

     REDIRECT PROTECTION

     ======================================================= */



  useEffect(() => {

    if (!exam) {

      navigate("/home", {

        replace: true,

      });

    }

  }, [exam, navigate]);



  /*

   * Payment must already be successful.

   */

  useEffect(() => {

    if (

      exam &&

      paymentStatus !== "SUCCESS" &&

      paymentStatus !== "Paid"

    ) {

      navigate("/payment", {

        replace: true,

      });

    }

  }, [exam, paymentStatus, navigate]);



  /* =======================================================

     DATE CHANGE

     ======================================================= */



  const handleDateChange = (selectedDate) => {

    setDate(selectedDate);

    setStartTime("");

    setScheduleError("");

    setScheduleSuccess("");

  };



  /* =======================================================

     TIME CHANGE

     ======================================================= */



  const handleTimeChange = (event) => {

    setStartTime(event.target.value);

    setScheduleError("");

    setScheduleSuccess("");

  };



  /* =======================================================

     CONFIRM SCHEDULE

     ======================================================= */



  const handleConfirmSchedule = async () => {

    setScheduleError("");

    setScheduleSuccess("");



    if (!exam?.id) {

      setScheduleError(

        "Examination information is missing."

      );

      return;

    }



    if (!registrationId) {

      setScheduleError(

        "Exam registration is missing."

      );

      return;

    }



    if (!date) {

      setScheduleError(

        "Please select an examination date."

      );

      return;

    }



    if (!startTime) {

      setScheduleError(

        "Please select an examination time."

      );

      return;

    }




    /* If today is selected, the selected time must still be in the future. */
    if (
      date === minDate &&
      startTime <= getCurrentTimeHHMM()
    ) {
      setScheduleError(
        "Please select a future time. You can schedule the examination for any time from now onward."
      );
      return;
    }

    setBusy(true);



    try {

      /*

       * Django creates the ExamSchedule using:

       *

       * exam_id

       * exam_date

       * start_time

       *

       * End time is calculated by the backend.

       */

      const response = await scheduleExam(

        exam.id,

        date,

        startTime

      );



      const data = response?.data || {};



      /*

       * Django returns the newly-created schedule.

       */

      const savedSchedule = {

        id:

          data.schedule_id ||

          data.id ||

          null,



        scheduleId:

          data.schedule_id ||

          data.id ||

          null,



        date:

          data.exam_date ||

          date,



        start_time:

          data.start_time ||

          startTime,



        end_time:

          data.end_time ||

          null,



        time:

          data.start_time ||

          startTime,



        status:

          data.status ||

          "VERIFICATION_PENDING",



        registration_id:

          data.registration_id ||

          registrationId,

      };



      /*

       * Save the schedule in ExamContext.

       */

      setSchedule(savedSchedule);



      /*

       * IMPORTANT:

       *

       * DO NOT open the verification modal here.

       *

       * The verification code may already be sent by email,

       * but the student should enter it only on the

       * examination day through the exam reminder.

       */



      const scheduledDate = toDisplayDate(

        savedSchedule.date

      );



      const scheduledTime = formatTime(

        savedSchedule.start_time

      );



      setScheduleSuccess(

        `Your examination is scheduled for ${scheduledDate} at ${scheduledTime}. All the best for your examination!`

      );



      /*

       * Give the student a short moment to see the

       * successful scheduling message, then return Home.

       */

      setTimeout(() => {

        navigate("/home", {

          replace: true,

        });

      }, 1800);



    } catch (err) {

      console.error(

        "Schedule error:",

        err

      );



      const backendData =

        err?.response?.data;



      setScheduleError(

        backendData?.detail ||

          backendData?.message ||

          "Unable to schedule the examination."

      );

    } finally {

      setBusy(false);

    }

  };



  /* =======================================================

     SAFETY

     ======================================================= */



  if (!exam) {

    return null;

  }



  /* =======================================================

     DISPLAY TIME

     ======================================================= */



  const displaySelectedTime = startTime

    ? formatTime(startTime)

    : "—";



  /* =======================================================

     UI

     ======================================================= */



  return (

    <div className="oes-page-full">



      <div className="oes-home-shell d-flex flex-row overflow-hidden">



        <AppSidebar />



        <div className="flex-grow-1 oes-page container">



          <div

            style={{

              maxWidth: 900,

              margin: "0 auto",

            }}

          >



            {/* =================================================

                HEADER

                ================================================= */}



            <div className="d-flex justify-content-between align-items-center mb-4">



              <div>



                <h2 className="h5 fw-bold mb-1">

                  Schedule Examination

                </h2>



                <div className="small text-muted">

                  {exam.name}

                </div>



              </div>



              <span className="badge bg-success">

                PAID

              </span>



            </div>



            {/* =================================================

                SUCCESS MESSAGE

                ================================================= */}



            {scheduleSuccess && (

              <div className="alert alert-success py-3 mb-3">



                <div className="d-flex align-items-start gap-2">



                  <i className="bi bi-check-circle-fill text-success mt-1" />



                  <div>



                    <div className="fw-semibold mb-1">

                      Exam Scheduled Successfully!

                    </div>



                    <div className="small">

                      {scheduleSuccess}

                    </div>



                  </div>



                </div>



              </div>

            )}



            {/* =================================================

                ERROR

                ================================================= */}



            {scheduleError && (

              <div className="alert alert-warning py-2 small">

                {scheduleError}

              </div>

            )}



            {/* =================================================

                DATE + TIME

                ================================================= */}



            <div className="row g-3 g-md-4">



              {/* ================= DATE ================= */}



              <div className="col-12 col-md-6">



                <div className="oes-card p-3 p-md-4 h-100">



                  <h2 className="h6 fw-bold mb-1">

                    Select Date

                  </h2>



                  <p className="small text-muted mb-3">

                    Select today or any future examination date.

                  </p>



                  <Calendar

                    minDate={minDate}

                    maxDate={maxDate}

                    value={date}

                    onChange={handleDateChange}

                  />



                </div>



              </div>



              {/* ================= TIME ================= */}



              <div className="col-12 col-md-6">



                <div className="oes-card p-3 p-md-4 h-100">



                  <h2 className="h6 fw-bold mb-1">

                    Select Time

                  </h2>



                  <p className="small text-muted mb-3">

                    Choose any future start time. If you select today, the time must be later than now.

                  </p>



                  {!date ? (



                    <div className="alert alert-light border small">

                      Select an examination date first.

                    </div>



                  ) : (



                    <div>



                      <label

                        htmlFor="exam-start-time"

                        className="form-label small fw-semibold"

                      >

                        Examination Start Time

                      </label>



                      <input

                        id="exam-start-time"

                        type="time"

                        className="form-control"

                        value={startTime}

                        min={minTime || undefined}
                        onChange={handleTimeChange}

                      />



                      <div className="small text-muted mt-2">

                        Your examination duration will be

                        calculated automatically from the

                        configured exam sections.

                      </div>



                    </div>



                  )}



                </div>



              </div>



            </div>



            {/* =================================================

                SELECTION SUMMARY

                ================================================= */}



            <div className="oes-card p-3 p-md-4 mt-3 mt-md-4">



              <h2 className="h6 fw-bold mb-2">

                Your Selection

              </h2>



              <table className="table table-borderless mb-0">



                <tbody>



                  <tr>



                    <td className="text-muted small">

                      Examination

                    </td>



                    <td className="fw-semibold">

                      : {exam.name}

                    </td>



                  </tr>



                  <tr>



                    <td className="text-muted small">

                      Date

                    </td>



                    <td className="fw-semibold">

                      : {toDisplayDate(date)}

                    </td>



                  </tr>



                  <tr>



                    <td className="text-muted small">

                      Time

                    </td>



                    <td className="fw-semibold">

                      : {displaySelectedTime}

                    </td>



                  </tr>



                </tbody>



              </table>



            </div>



            {/* =================================================

                CONFIRM

                ================================================= */}



            <div className="oes-card p-3 p-md-4 mt-3 mt-md-4">



              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">



                <div className="d-flex align-items-start gap-2 small text-muted">



                  <i className="bi bi-envelope text-primary mt-1" />



                  <span>

                    A verification code will be sent to

                    your registered email. You will need

                    this code on the examination day.

                  </span>



                </div>



                <button

                  className="btn btn-primary px-4"

                  disabled={

                    busy ||

                    !date ||

                    !startTime

                  }

                  onClick={handleConfirmSchedule}

                  style={{

                    minWidth: 180,

                  }}

                >

                  {busy

                    ? "Scheduling..."

                    : "CONFIRM EXAM"}

                </button>



              </div>



            </div>



          </div>



        </div>



      </div>



    </div>

  );

}